import { FunctionComponent } from 'react';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { PanelGroup, Panel } from '@cbridge-tools/resizable-panels';
import { ConfigPageLayout, SideFilterPanel, usePersistedPanelLayout, panelLayoutSettingKey } from '@cbridge-modules/layout';
import { UserSettingsPage } from '@cbridge-packages/user-settings';
import { DashboardSidebarSubNavigation } from '@cbridge-modules/sidebar';
import { ErrorInvalidFilter } from '@cbridge-modules/error-invalid-filter';
import { RulesManagementAction } from '@cbridge-modules/rules-management-page';
import { isMatchedPathnameSelector } from '@cbridge-packages/routing';
import { CoverageServerListSubscriber } from '@cbridge-packages/coverage-server';
import { CoverageReportListSubscriber } from '@cbridge-packages/coverage-report';
import {
  RouteSegments,
  generateCoverageReportRoute,
  useConfigExternalReportingNavigationItems,
} from '@cbridge-packages/routing-client-admin';
import { ExternalReportingNavigation } from '../navigation/ExternalReportingNavigation';
import { externalReportingLayoutPropsSelector } from './ExternalReportingLayout.selector';

// Matches the rules pages' side panel floor (SidePanel `panelSizes.left.minSize`), so the
// navigation cannot be squeezed to half the width other pages allow.
const NAV_MIN_SIZE = 10;

// Accounts arrive embedded in the reports load, so there is no account subscriber.
export const ExternalReportingLayout: FunctionComponent = () => {
  const dispatch = useDispatch();
  const navigationItems = useConfigExternalReportingNavigationItems();
  const { hasInvalidRules, hasActiveFilters } = useSelector(externalReportingLayoutPropsSelector);

  const reportsBase = generateCoverageReportRoute();
  const isReportsActive = useSelector(isMatchedPathnameSelector, { pattern: reportsBase });

  const handleClearAllFilters = () => {
    dispatch(RulesManagementAction.resetValidityFilter());
  };

  const sideLayoutProps = usePersistedPanelLayout(panelLayoutSettingKey(UserSettingsPage.ConfigExternalReporting, 'side'));

  return (
    <>
      <CoverageServerListSubscriber />
      <CoverageReportListSubscriber />
      <ConfigPageLayout navMinSize={NAV_MIN_SIZE} settingsKey={UserSettingsPage.ConfigExternalReporting}>
        <DashboardSidebarSubNavigation
          items={navigationItems}
          variant={'secondary'}
          basePath={`/${RouteSegments.Root.Config}/${RouteSegments.Config.ExternalReporting}`}
          isMini={false}
          isFullyExpanded={true}
        />
        {isReportsActive && (
          // No defaultSize on either panel: a fixed split pinned the filters to the bottom of
          // the sidebar with a large gap above them. They stay resizable by dragging the divider.
          <PanelGroup direction={'vertical'} style={{ flex: 1, minHeight: 0 }} {...sideLayoutProps}>
            <Panel id={'list'} minSize={20}>
              <ExternalReportingNavigation />
            </Panel>
            {hasInvalidRules && (
              <SideFilterPanel
                collapsible={true}
                onClearAllFilters={handleClearAllFilters}
                hasActiveFilters={hasActiveFilters}
              >
                <ErrorInvalidFilter />
              </SideFilterPanel>
            )}
          </PanelGroup>
        )}
      </ConfigPageLayout>
    </>
  );
};
